import { ref, computed } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { workflowService } from '../../services/workflowService.js'
import { invalidateNavBadges } from '../ui/useNavBadges.js'
import { useCurrentUser } from '../auth/useCurrentUser.js'

/**
 * Composable for managing the rejection modal
 * @param {Object} options
 * @param {Function} options.refetch - Function to refetch prospects
 */
export function useRejectModal({ refetch }) {
  const queryClient = useQueryClient()
  const { getApiUserInfo } = useCurrentUser()

  const showRejectModal = ref(false)
  const selectedProspectForReject = ref(null)
  const isRejecting = ref(false)
  const rejectError = ref('')

  function openRejectModal(prospect, event) {
    event?.stopPropagation()
    selectedProspectForReject.value = prospect
    rejectError.value = ''
    showRejectModal.value = true
  }

  function closeRejectModal() {
    showRejectModal.value = false
    selectedProspectForReject.value = null
    rejectError.value = ''
  }

  async function handleRejectSubmit(reason) {
    isRejecting.value = true
    rejectError.value = ''

    try {
      await workflowService.sourcingReject(
        selectedProspectForReject.value.id,
        getApiUserInfo(),
        reason
      )

      closeRejectModal()
      invalidateNavBadges(queryClient, { beoordelen: true, concretiseren: true })
      refetch()
      return true
    } catch (error) {
      console.error('Error rejecting prospect:', error)
      rejectError.value = error.response?.data?.error || 'Fout bij afwijzen'
      return false
    } finally {
      isRejecting.value = false
    }
  }

  const rejectModalProps = computed(() => ({
    open: showRejectModal.value,
    prospect: selectedProspectForReject.value,
    isSubmitting: isRejecting.value,
    error: rejectError.value
  }))

  return {
    showRejectModal,
    selectedProspectForReject,
    isRejecting,
    rejectError,
    openRejectModal,
    closeRejectModal,
    handleRejectSubmit,
    rejectModalProps
  }
}
